import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useState, useMemo, useRef } from 'react';
import { ExternalLink, Pencil, ArrowLeft, Settings, Package, Plus, CheckCircle, Send, Mail, Trash2, Ban, XCircle, MoreHorizontal, Eye, Info, X, AlertTriangle, MailX, Combine } from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout.tsx';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { PaymentModal } from '@/components/invoices/PaymentModal';
import { AuditHistoryTimeline } from '@/components/invoices/AuditHistoryTimeline';
import { AddItemModal } from '@/components/invoices/AddItemModal';
import { InvoicePDFDownload } from '@/components/invoices/InvoicePDFDownload';
import { SendInvoiceButton } from '@/components/invoices/SendInvoiceButton';
import { UnconfirmedSendBanner } from '@/components/invoices/UnconfirmedSendBanner'; // v0.17: settle an unconfirmed send
import { AccountingContactsList } from '@/components/accounting-contacts/AccountingContactsList';
import { AddInvoiceRecipientPopover } from '@/components/invoices/AddInvoiceRecipientPopover';
// R4 (v0.14): consolidated BILL TO panel replaces the legacy block at
// lines 734-1649. See R4-bill-to-mockups-2026-05-25.md Variant A.
import { BillToPanel, isBillToEditable } from '@/components/invoices/BillToPanel';
// R2 (SPEC-v017-completion-2026-09-27): the "who does this go to" lookup.
import { InvoiceRecipientsCard } from '@/components/invoices/InvoiceRecipientsCard';
import type { RecipientLookup } from '@/components/invoices/InvoiceRecipientsCard';
import type {
  ScopeState,
  AddressState,
  LegalNameState,
  RecipientMeta,
  IdentityResolutionDto,
} from '@/components/invoices/BillToPanel';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { EntityActionMenu } from '@/components/ui/EntityActionMenu';
import { EmailSendIndicator } from '@/components/email/EmailSendIndicator';
import { computeServiceTaxRows } from '@/lib/invoiceTaxRows';
import { changedAfterSendLine, contentChangedAfterSend, isSent } from '@/lib/sendEvidence';
import { DropboxSyncIndicator } from '@/components/invoices/DropboxSyncIndicator';
import type { ActionMenuItem } from '@/components/ui/EntityActionMenu';
import { InvoiceStatusSelect } from '@/components/forms/InvoiceStatusSelect'; // Phase 6 Batch 5.3
import { InvoiceStatusBadge } from '@/components/invoices/InvoiceStatusBadge'; // Phase 6 Batch 5.3: Consistency fix
import { InvoiceNotesSection } from '@/components/invoices/InvoiceNotesSection'; // Phase 6: PDF notes
import { InvoicePoNumberField } from '@/components/invoices/InvoicePoNumberField'; // SPEC-project-po-number-2026-08-17 R2
import { InvoiceItemRow } from '@/components/invoices/InvoiceItemRow'; // Phase 9.15a: Inline editing
import { TaxClassInfoTooltip } from '@/components/invoices/TaxClassInfoTooltip'; // v0.17 taxclass-light: mode-aware ⓘ tooltip
import { InvoiceReceiptsSection } from '@/components/invoices/InvoiceReceiptsSection'; // Phase 10b.2: Receipt display
import { ScheduleFollowupModal } from '@/components/invoices/ScheduleFollowupModal'; // R3: Collection follow-ups
import { FollowupsSection } from '@/components/invoices/FollowupsSection'; // R3: Collection follow-ups
import { InvoicePaymentHistory } from '@/components/invoices/InvoicePaymentHistory'; // V172: payment ledger
import { InvoiceDocumentHistory } from '@/components/invoices/InvoiceDocumentHistory';
import { InvoiceMergeFlow } from '@/components/invoices/InvoiceMergeFlow'; // SPEC-invoice-merge-history R4: Review merge
import { useFeature } from '@/lib/features/FeatureProvider'; // reviewed_invoice_merge tenant flag
import { formatMergeMoney } from '@/lib/invoice-merge-guidance';
import { EditContactModal } from '@/components/contacts/EditContactModal'; // Phase 9.15b: Edit contact from invoice
import { AddContactModal } from '@/components/contacts/AddContactModal'; // 9.18j: Add contact from invoice
import type { InvoiceStatus } from '@/components/forms/InvoiceStatusSelect'; // Phase 6 Batch 5.3
import { SuperAdminDestructiveConfirmDialog } from '@/components/invoices/SuperAdminDestructiveConfirmDialog'; // super-admin destructive-op guard
import { CancelInvoiceDialog } from '@/components/invoices/CancelInvoiceDialog'; // cancel-invoice feature
import { getDeleteBlockedReason } from '@/lib/invoice-delete'; // Christa-lens redirect: shared Delete-disable reason
import { useAuth } from '@/lib/auth/AuthProvider'; // super-admin destructive-op guard
import { isGuardedStatusDowngrade, statusLabel } from '@/lib/superAdminDestructiveGuard'; // super-admin destructive-op guard
import { z } from 'zod';
import { toast } from 'sonner';
import { mutationErrorToast } from '@/lib/globalErrorHandlers';
import { invoiceSchema, invoiceItemSchema } from '@/lib/schemas';
import { useAccountingContacts } from '@/hooks/useAccountingContacts';
import { api, DocumentBytesUnavailableError } from '@/lib/api';
import { formatDateOnly } from '@/lib/utils';
import { invalidateProjectListQueries } from '@/lib/cache-utils';
import { useUpdateInvoiceInline, useVoidPayment, useDeleteInvoice, useCancelInvoice, useReferrerPage, useUpdateOrganization, useCreateOrganizationEstablishment, useUpdateOrganizationEstablishment, useUpdateProjectInline, useBillableItemFileCounts, useRemoveInvoiceContact, useAddInvoiceContact, useTaxEngineStatus } from '@/hooks'; // Phase 6 Batch 5.3 + Phase 6 Void Payment + 9.15c + 9.18i + 9.18j + 9.19e + 10b.2 + bill-to fix 2026-05 (hotfix2: dropped useUpdateProjectScope; see Bug #5) + v0.17 taxclass-light: header ⓘ tooltip + cancel-invoice feature
import { useVoidInvoicePayment } from '@/hooks/useInvoicePayment'; // V172: void with reason + payment_id
import { useInvoicePayments } from '@/hooks/useInvoicePayments'; // V172: payment ledger
import type { AccountingContact } from '@/lib/schemas';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '@/components/ui/tooltip';
import { Input } from '@/components/ui/input';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
} from '@/components/ui/dropdown-menu';

type Invoice = z.infer<typeof invoiceSchema>;
type InvoiceItem = z.infer<typeof invoiceItemSchema>;

export function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [markSentOpen, setMarkSentOpen] = useState(false);
  const [markSentRevisionId, setMarkSentRevisionId] = useState('');
  const [documentActionBusy, setDocumentActionBusy] = useState(false);
  // SPEC-invoice-merge-history R3: saved copies whose stored bytes turned out to be gone.
  const [unavailableRevisionIds, setUnavailableRevisionIds] = useState<ReadonlySet<string>>(() => new Set());
  const [addItemModalOpen, setAddItemModalOpen] = useState(false);
  const [isEditingStatus, setIsEditingStatus] = useState(false); // Phase 6 Batch 5.3: Inline editing
  const [showVoidConfirm, setShowVoidConfirm] = useState(false); // Phase 6: Void payment confirmation
  const [voidAmount, setVoidAmount] = useState(''); // QF-07: Partial void amount
  const [isEditingDate, setIsEditingDate] = useState(false); // QF-09: Inline date editing
  const [editedDate, setEditedDate] = useState(''); // QF-09: Edited date value
  const [editContactModalOpen, setEditContactModalOpen] = useState(false); // 9.15b: Edit contact modal
  const [removeItemId, setRemoveItemId] = useState<string | null>(null); // 9.18b: Remove item confirmation
  const [isEditingEstablishment, setIsEditingEstablishment] = useState(false); // 9.18i: Establishment inline editing
  const [isEditingLegalName, setIsEditingLegalName] = useState(false); // 9.18i: Legal entity inline editing
  const [editedLegalName, setEditedLegalName] = useState(''); // 9.18i: Legal entity edited value
  const [isEditingEstablishmentLegalName, setIsEditingEstablishmentLegalName] = useState(false); // 9.26: Establishment legal name inline editing
  const [editedEstablishmentLegalName, setEditedEstablishmentLegalName] = useState(''); // 9.26: Establishment legal name edited value
  const [showEstablishmentLegalConfirm, setShowEstablishmentLegalConfirm] = useState(false); // 9.26: Establishment legal name confirmation
  const [pendingEstablishmentLegalValue, setPendingEstablishmentLegalValue] = useState(''); // 9.26: Pending establishment legal name
  const [isEditingDueDate, setIsEditingDueDate] = useState(false); // 9.18j: Due date inline editing
  const [editedDueDate, setEditedDueDate] = useState(''); // 9.18j: Due date edited value
  const [isCreatingEstablishment, setIsCreatingEstablishment] = useState(false); // 9.18j: Creating new establishment
  const [newEstablishmentName, setNewEstablishmentName] = useState(''); // 9.18j: New establishment name
  const [newEstablishmentLegalName, setNewEstablishmentLegalName] = useState(''); // Legal entity name for new establishment
  const [showEstablishmentConfirm, setShowEstablishmentConfirm] = useState(false); // 9.18j: Establishment name edit confirmation
  const [editingEstablishmentId, setEditingEstablishmentId] = useState<number | null>(null); // 9.18j: Which establishment is being edited
  const [pendingEstablishmentName, setPendingEstablishmentName] = useState(''); // 9.18j: Pending establishment name change
  const [showAddContactModal, setShowAddContactModal] = useState(false); // 9.18j: Add contact modal
  const [showContactEditConfirm, setShowContactEditConfirm] = useState(false); // 9.18j: Contact edit confirmation
  const [isSelectingContact, setIsSelectingContact] = useState(false); // 9.18j: Contact selection dropdown
  const [isEditingInvoiceNumber, setIsEditingInvoiceNumber] = useState(false); // 9.18j: Invoice number inline editing
  const [editedInvoiceNumber, setEditedInvoiceNumber] = useState(''); // 9.18j: Invoice number edited value
  // 9.21g: Billing address source selector
  const [isSelectingAddressSource, setIsSelectingAddressSource] = useState(false);
  const [manageAccountingOpen, setManageAccountingOpen] = useState(false);
  // Action bar consolidation: Controlled send dialog for overflow menu
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sendDialogInitialTab, setSendDialogInitialTab] = useState<'edit' | 'preview' | 'pdf' | 'history'>('edit');
  // R1 (B2): the hidden SendInvoiceButton instance below is shared by three
  // entry points (Preview PDF, Resend from overflow menu, View History from
  // EmailSendIndicator). Only the Preview PDF entry point must not be able to
  // send — see SendInvoiceButton's `previewOnly` prop docstring.
  const [sendDialogPreviewOnly, setSendDialogPreviewOnly] = useState(false);
  const [isEditingCustomAddress, setIsEditingCustomAddress] = useState(false);
  const [editedCustomAddress, setEditedCustomAddress] = useState('');
  // 9.32: Inline per-invoice address override editor (BILL TO card)
  const [isEditingAddressOverride, setIsEditingAddressOverride] = useState(false);
  const [addressOverrideMode, setAddressOverrideMode] = useState<'project' | 'custom' | 'establishment'>('project');
  const [editedAddressOverride, setEditedAddressOverride] = useState('');
  const [showAddressConfirm, setShowAddressConfirm] = useState(false);
  const [pendingAddressValue, setPendingAddressValue] = useState('');
  const [pendingAddressSource, setPendingAddressSource] = useState<'organization' | 'project' | null>(null);
  const [billToEstablishmentEditing, setBillToEstablishmentEditing] = useState(false); // R3: Bill To block establishment edit
  // bill-to fix 2026-05: Inline "Why am I seeing this?" disclosure for the
  // empty-TO fallback warning. Hidden by default, expanded on user request.
  const [showFallbackExplanation, setShowFallbackExplanation] = useState(false);
  // 9.31c: Pre-edit scope-warning modal for legal-name editing (org-wide / DBA-wide impact)
  const [showLegalNameScopeWarning, setShowLegalNameScopeWarning] = useState<null | 'organization' | 'establishment'>(null);
  // Hotfix2 — Bug #5: removed dead state for the v0.12.22 Fix 2 / Fix 4
  // pre-R4 propagation dialogs (showScopeEstablishmentDialog,
  // pendingEstablishmentChange, showAddressToProjectDialog,
  // pendingProjectAddress). R4 replaced both flows with PropagationModal +
  // BillToPanel; the legacy state setters were never called from any code
  // path. Dialog JSX deleted below too. Same for useUpdateProjectScope,
  // which was only referenced from the deleted scope-establishment dialog.
  const updateInvoiceInline = useUpdateInvoiceInline(); // Phase 6 Batch 5.3
  const updateOrganization = useUpdateOrganization(); // 9.18i: Update organization for legal entity
  const updateProject = useUpdateProjectInline(); // 9.21g: Update project address
  const createEstablishment = useCreateOrganizationEstablishment(); // 9.18j: Create establishment
  const updateEstablishment = useUpdateOrganizationEstablishment(); // 9.18j: Update establishment
  const { getBackToListUrl } = useReferrerPage('invoices'); // 9.15c: Back navigation with page
  const voidPaymentMutation = useVoidPayment(); // Phase 6: Void payment (legacy — kept for backward compat)
  const deleteMutation = useDeleteInvoice(); // Shared hook with error toasts
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // Cancel-invoice feature.
  const cancelMutation = useCancelInvoice();
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  // Super-admin destructive-op guard on cancel: mirrors pendingDowngradeStatus
  // below, but dedicated to the cancel flow (a different mutation, a different
  // opDescription) rather than overloading the status-select one.
  const [pendingCancelSuperAdmin, setPendingCancelSuperAdmin] = useState<{ reason?: string } | null>(null);
  // Christa-lens redirect (2026-09-15): the dialog's inline error slot existed
  // but nothing ever set it -- a refused cancel (e.g. payment recorded) only
  // ever showed as a toast, with no explanation inside the dialog itself.
  const [cancelError, setCancelError] = useState<{ message: string } | null>(null);
  const [scheduleFollowupOpen, setScheduleFollowupOpen] = useState(false); // R3: Collection follow-ups
  const removeInvoiceContact = useRemoveInvoiceContact();
  // bill-to fix 2026-05: One-click promote of the silent Primary fallback to an
  // explicit TO recipient so the BILL TO UI matches what the PDF renders.
  const addInvoiceContact = useAddInvoiceContact();
  // V172: void dialog with reason + payment picker
  const [showVoidPaymentDialog, setShowVoidPaymentDialog] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [selectedPaymentId, setSelectedPaymentId] = useState<string | undefined>(undefined);
  // 2026-05-30 audit fix: re-entry guard for void submission (same pattern
  // PaymentModal got earlier today). Button.disabled={isPending} alone races
  // a rapid second click before React re-renders. submittingRef beats the
  // race even when the global mutation retry was on (now off, but belt + braces).
  const voidSubmittingRef = useRef(false);
  // Super-admin destructive-op guard: super-admins must confirm guarded
  // destructive ops (status downgrades + payment voids) on production data, then
  // the request carries the X-Super-Admin-Confirm-Destructive header (else the
  // backend 403s). Non-super-admins are unaffected (no modal, current flow).
  const { user, can } = useAuth();
  // Christa-lens redirect (2026-09-15): cancelling needs can_delete_invoices
  // (admin-only by default, per auth/permissions.py's ROLE_PERMISSION_DEFAULTS)
  // -- same permission Delete should have required all along but never did.
  // Gate both the same way rather than leaving Delete ungated.
  const canDeleteInvoices = can('can_delete_invoices');
  // v0.17: settling an unconfirmed invoice email needs the send permission.
  const canEditInvoices = can('can_edit_invoices');
  const isSuperAdmin = !!user?.is_super_admin;
  // Pending guarded status downgrade awaiting super-admin confirm.
  const [pendingDowngradeStatus, setPendingDowngradeStatus] = useState<InvoiceStatus | null>(null);
  // V172 hooks — id may be undefined until route resolves; hooks are enabled only when id is present
  const voidInvoicePaymentMutation = useVoidInvoicePayment(id ?? '');
  const { data: paymentsData } = useInvoicePayments(id ?? '', { enabled: !!id });

  // Phase 9.19e: Combined query - fetches invoice, items, establishments, and contacts in one API call
  // This reduces page load from 4 API calls to 1, eliminating waterfall dependencies
  const { data: invoiceData, isLoading: invoiceLoading, error: invoiceError } = useQuery<{
    invoice: Invoice;
    invoice_items: InvoiceItem[];
    establishments: Array<{ id: number; name: string; is_default: boolean }>;
    contacts: Array<{ id: string; name: string; email?: string; phone?: string; is_primary?: boolean }>;
    invoice_contacts: Array<{ id?: string; contact_id?: string; contact_name: string; contact_email: string; role: string }>;
    // bill-to fix 2026-05: see api.invoices.get zod schema for shape rationale.
    // V175 (bill-to suppress): suppress_attention_line is the per-invoice opt-out.
    to_contacts_meta?: {
      explicit_count: number;
      fallback_active: boolean;
      fallback_contact: { contact_name: string; contact_email: string } | null;
      suppress_attention_line?: boolean;
    };
    // R3a (v0.14): meta blocks consumed by the new BillToPanel.
    scope_state?: ScopeState | null;
    address_state?: AddressState | null;
    legal_name_state?: LegalNameState | null;
    recipients_meta?: RecipientMeta[];
    // R4 (invoice-identity-cascade): R2's resolution bundle for the Christa-Land
    // BILL TO surface. Typed as `unknown` in this already-large useQuery generic
    // (TanStack's overload resolver tips into TQueryFnData=unknown if this
    // object grows with a deep type); the typed view is derived below via
    // `resolutionForPanel`.
    identity_resolution?: unknown;
    // R2 (SPEC-v017-completion-2026-09-27): who this invoice goes to, resolved
    // past invoice_contacts with a label saying where each address came from.
    // Held as `unknown` for the same reason identity_resolution above is: this
    // useQuery generic is already large enough that TanStack's overload
    // resolver tips into TQueryFnData=unknown when a deep object type is added
    // to it. Narrowed to the DTO at the one render boundary below.
    recipient_lookup?: unknown;
  }>({
    queryKey: ['invoices', id, 'full'],
    queryFn: async () => {
      const result = await api.invoices.get(id!);
      return {
        invoice: result.invoice,
        invoice_items: result.invoice_items || [],
        establishments: result.establishments || [],
        contacts: result.contacts || [],
        invoice_contacts: result.invoice_contacts || [],
        to_contacts_meta: result.to_contacts_meta,
        // R3a (v0.14): pass meta blocks through — BillToPanel consumes them.
        scope_state: (result as any).scope_state ?? null,
        address_state: (result as any).address_state ?? null,
        legal_name_state: (result as any).legal_name_state ?? null,
        recipients_meta: (result as any).recipients_meta ?? [],
        // R4: resolution bundle (now declared in the get() Zod schema, so it
        // survives the boundary — no [Zod-strip]). Read via a typed view of the
        // carrier (the field IS declared on the schema; a structural cast, NOT
        // an untyped escape hatch). Held as `unknown` downstream to keep
        // useQuery's overload inference stable; narrowed to the DTO at render.
        identity_resolution:
          (result as { identity_resolution?: unknown }).identity_resolution ?? null,
        // R2: declared on the get() Zod schema, so this survives the boundary.
        recipient_lookup:
          (result as { recipient_lookup?: unknown }).recipient_lookup ?? null,
      };
    },
    enabled: !!id,
    staleTime: 30_000, // 30s — prevent refetch-on-navigate-back from overwriting setQueryData edits
  });

  // Extract data from combined response
  const invoice = invoiceData?.invoice;
  const documentHistoryQuery = useQuery({
    queryKey: ['invoices', id, 'document-history'],
    queryFn: () => api.invoices.documentHistory(id!),
    enabled: !!id,
  });
  // SPEC-invoice-merge-history R4 AC1: Review merge starts here for an invoice in ANY of the six states.
  const reviewedMerge = useFeature('reviewed_invoice_merge');
  const [showMergeReview, setShowMergeReview] = useState(false);
  // R3: the merged, source-labelled timeline only exists for an invoice with merge ancestry.
  const hasMergeHistory = documentHistoryQuery.data?.lineage?.has_merge_history === true;
  const lineageQuery = useInfiniteQuery({
    queryKey: ['invoices', id, 'lineage-history'],
    queryFn: ({ pageParam }) => api.invoices.lineageHistory(id!, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    enabled: !!id && hasMergeHistory,
  });
  const lineageEvents = lineageQuery.data?.pages.flatMap((page) => page.events) ?? [];
  const items = invoiceData?.invoice_items || [];
  const establishmentsData = invoiceData?.establishments || [];
  const contactsData = invoiceData?.contacts || [];
  // R4 (invoice-identity-cascade): the typed view of R2's resolution bundle for
  // the BillToPanel. `invoiceData.identity_resolution` is held as `unknown` on
  // the query (keeps useQuery's overload inference stable); narrow it to the FE
  // DTO at this one boundary via a typed view of the carrier (a structural cast
  // through the declared carrier shape — NOT an untyped escape hatch — so the
  // field isn't a Zod-stripped phantom: it's declared in api.invoices.get).
  const resolutionForPanel: IdentityResolutionDto | null =
    (invoiceData as { identity_resolution?: unknown } | undefined)
      ?.identity_resolution as IdentityResolutionDto | null ?? null;

  // R2 (SPEC-v017-completion-2026-09-27): the typed view of the recipient
  // lookup, narrowed at this one boundary through the declared carrier shape
  // (a structural cast, NOT an untyped escape hatch — the field IS declared on
  // api.invoices.get's Zod schema, so it is not a phantom). Same pattern as
  // resolutionForPanel directly above, and for the same TanStack reason.
  const recipientLookup: RecipientLookup | null =
    (invoiceData as { recipient_lookup?: unknown } | undefined)
      ?.recipient_lookup as RecipientLookup | null ?? null;

  // Theme 5b: doomed-affordance gating for Mark Ready / Mark Sent.
  // The backend 400s those transitions on a $0 / no-item / sum-mismatched
  // invoice; compute the same condition here so we can disable the affordances
  // (status dropdown options + overflow menu items) instead of letting the user
  // pick them and eat a 400. All data is already on the page.
  const lineItemsSum = useMemo(
    () => items.reduce(
      (sum: number, i: any) => sum + (Number(i.amount) || 0) + (Number(i.tax_amount) || 0),
      0,
    ),
    [items],
  );
  // R4 (invoice-identity-cascade) AC6: the unresolved `needs a client` gate.
  // BillToPanel computes it from R2 resolution + reports via onGateStateChange;
  // we hold it here so it disables the actions Christa actually uses to push an
  // invoice out — PDF download AND mark-as-sent. The REACT disable is the
  // VISIBLE layer over R1's SERVER gate at the PDF path + the
  // status-transition-to-`sent` PUT; the server is the real boundary.
  const [identityGateBlocked, setIdentityGateBlocked] = useState(false);

  // R2 (SPEC-v017-completion-2026-09-27): the ONE action that fixes an empty
  // recipient lookup is recording an address, and that already lives in BILL
  // TO's "Add recipient" popover — which carries the propagation choice ("this
  // invoice / this project / this client") that makes the address stick for
  // NEXT month's batch. Re-implementing it in the lookup card would fork those
  // semantics, and forked recipient-propagation semantics is the bug class this
  // codebase has already paid for twice. So the card's button drives the real
  // affordance instead of duplicating it: scroll it into view and open it.
  //
  // QA round 2 (MED-1): it returns whether it actually opened something. When
  // BILL TO's trigger is not on the page the click used to be swallowed in
  // silence — on an invoice she is chasing payment on, that is the worst
  // possible moment for a dead link. The card now says where to record the
  // address instead.
  const focusAddRecipient = (): boolean => {
    const trigger = document.querySelector<HTMLElement>(
      '[data-testid="bill-to-add-recipient"]',
    );
    if (!trigger) return false;
    trigger.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    trigger.click();
    return true;
  };

  const readySentBlocked = useMemo(() => {
    if (identityGateBlocked) {
      return 'This invoice needs a client before it can be sent.';
    }
    if (items.length === 0) {
      return 'Add line items before marking this invoice ready or sent.';
    }
    const total = Number(invoice?.total_amount) || 0;
    if (Math.abs(lineItemsSum - total) > 0.01) {
      return 'Invoice total does not match the sum of line items. Fix the totals first.';
    }
    return undefined;
  }, [identityGateBlocked, items.length, lineItemsSum, invoice?.total_amount]);

  // R2: Invoice contacts snapshot from GET /api/invoices/{id} response.
  // Maps invoice_contacts rows to AccountingContact shape for SendInvoiceButton compatibility.
  // TO contacts first, CC contacts second — matches the order returned by the backend.
  // The snapshot rows only carry name + email; the full AccountingContact schema fields we don't
  // need (organization_id, created_at, etc.) are not used by SendInvoiceButton, so we cast.
  // R3: Raw invoice_contacts with role field for Bill To block TO/CC display
  const invoiceContacts = (invoiceData?.invoice_contacts ?? []) as Array<{
    id?: string;
    contact_id?: string;
    contact_name: string;
    contact_email: string;
    role: string;
  }>;

  // bill-to fix 2026-05: TO fallback meta drives the BILL TO warning block when
  // the PDF will silently substitute the org's Primary contact in Attention.
  const toContactsMeta = invoiceData?.to_contacts_meta;

  const resolvedAccountingContacts = ((invoiceData?.invoice_contacts ?? []) as Array<{
    id?: string;
    contact_id?: string;
    contact_name: string;
    contact_email: string;
    role: string;
  }>).map((ic) => ({
    id: ic.contact_id ?? '',
    organization_id: '',
    name: ic.contact_name,
    emails: ic.contact_email ? [ic.contact_email] : [],
    created_at: '',
    updated_at: '',
  })) as AccountingContact[];

  // v0.12.23 Item 4: Surface establishment-level accounting contacts in the
  // BILL TO card so Christa knows who's on file without opening the manager.
  const { data: accountingContactsData } = useAccountingContacts(
    invoice?.organization_id ?? undefined,
    invoice?.establishment_id ?? undefined,
    { enabled: !!invoice?.organization_id, staleTime: 60_000 }
  );
  const accountingContacts: Array<{ name?: string; emails?: string[] }> =
    (accountingContactsData?.contacts ?? []) as any;

  // Phase 10b.2: Fetch receipt counts for expense items
  const expenseCategories = ['billable_expense', 'pass_through'];
  const expenseItemBillableIds = items
    .filter(item => expenseCategories.includes(item.line_category || item.billable_item_category || '') && item.billable_item_id)
    .map(item => item.billable_item_id!)
    .filter((id, i, arr) => arr.indexOf(id) === i); // deduplicate
  const { data: receiptCounts } = useBillableItemFileCounts(expenseItemBillableIds);

  // v0.17 taxclass-light: mode-aware copy for the "Tax class" column headers'
  // ⓘ tooltip. Absent/loading/errored settings fall through
  // taxClassTooltipCopy's own fail-closed default (undefined context -> not live).
  const { data: taxEngineStatus } = useTaxEngineStatus();
  const taxEngineContext = taxEngineStatus
    ? { tax_engine: taxEngineStatus.tax_engine, rules: taxEngineStatus.rules }
    : undefined;

  // Christa feedback 2026-04-08: warn when a draft invoice is dated >14 days after the
  // newest scheduled_month on its line items — she repeatedly forgets to back-date.
  const { shouldShowDateWarning, newestLineItemMonthLabel } = useMemo(() => {
    if (!invoice || invoice.status !== 'draft') {
      return { shouldShowDateWarning: false, newestLineItemMonthLabel: '' };
    }

    // Reflect in-progress edits so the warning dismisses as the user back-dates.
    const currentInvoiceDateStr = isEditingDate ? editedDate : invoice.invoice_date;
    if (!currentInvoiceDateStr) {
      return { shouldShowDateWarning: false, newestLineItemMonthLabel: '' };
    }

    const scheduledMonths = items
      .map(item => (item as { scheduled_month?: string | null }).scheduled_month)
      .filter((m): m is string => typeof m === 'string' && m.length > 0)
      .sort();

    if (scheduledMonths.length === 0) {
      return { shouldShowDateWarning: false, newestLineItemMonthLabel: '' };
    }

    const newestMonth = scheduledMonths[scheduledMonths.length - 1];
    // newestMonth is YYYY-MM-DD (typically the 1st of the month). Compute the LAST day
    // of that month so invoices dated within ~2 weeks of month-end don't trigger.
    const newestMonthDate = new Date(newestMonth + 'T00:00:00');
    const lastDayOfMonth = new Date(
      newestMonthDate.getFullYear(),
      newestMonthDate.getMonth() + 1,
      0
    );

    const invoiceDate = new Date(currentInvoiceDateStr + 'T00:00:00');
    const diffDays = Math.floor(
      (invoiceDate.getTime() - lastDayOfMonth.getTime()) / (1000 * 60 * 60 * 24)
    );

    const shouldShow = diffDays > 14;
    const monthLabel = newestMonthDate.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric',
    });

    return { shouldShowDateWarning: shouldShow, newestLineItemMonthLabel: monthLabel };
  }, [invoice, items, editedDate, isEditingDate]);

  // Status transition mutation
  const updateStatusMutation = useMutation({
    mutationFn: async ({ status, revisionId }: { status: string; revisionId?: string | null }) => {
      return api.invoices.update(id!, {
        status,
        ...(revisionId ? { external_send_revision_id: revisionId } : {}),
      });
    },
    onSuccess: async () => {
      // Phase 9.19e: Invalidate combined query — await to ensure fresh data
      await queryClient.invalidateQueries({ queryKey: ['invoices', id, 'full'] });
      await queryClient.invalidateQueries({ queryKey: ['invoices', id, 'document-history'] });
      setMarkSentOpen(false);
      setMarkSentRevisionId('');
    },
    // 2026-08-03: this mutation had onSuccess only. The global mutation handler
    // (src/lib/globalErrorHandlers.ts) early-returns when a mutation defines its
    // own onError, so with none defined the overflow-menu "Mark as Ready" /
    // "Mark as Sent" path fell through to the GENERIC "Your change was not
    // saved" toast and the backend's actual reason ("Cannot mark invoice as
    // ready or sent with no line items") was dropped. The sibling path through
    // InvoiceStatusSelect → useUpdateInvoiceInline has always surfaced the real
    // message; match it, including duration: Infinity so a rejected status
    // change can't flash and vanish before it's read.
    onError: (err: any) => {
      mutationErrorToast(err, 'Failed to update invoice status');
    },
  });

  // Remove invoice item mutation
  const removeItemMutation = useMutation({
    mutationFn: async (itemId: string) => {
      return api.invoiceItems.delete(itemId);
    },
    onSuccess: (_, deletedItemId) => {
      // Update detail cache directly by removing the item.
      // Cloud Run multi-instance architecture means GET-after-DELETE can return stale data.
      queryClient.setQueryData(['invoices', id, 'full'], (old: any) => {
        if (!old) return old;
        const remainingItems = (old.invoice_items || []).filter(
          (item: any) => item.id !== deletedItemId
        );
        const feeAmount = remainingItems.reduce((sum: number, i: any) => sum + (i.amount || 0), 0);
        const taxAmount = remainingItems.reduce((sum: number, i: any) => sum + (i.tax_amount || 0), 0);
        return {
          ...old,
          invoice_items: remainingItems,
          invoice: {
            ...old.invoice,
            fee_amount: feeAmount,
            tax_amount: taxAmount,
            total_amount: feeAmount + taxAmount,
          },
        };
      });

      // Also update invoice list cache so navigating back shows correct totals
      const detailData = queryClient.getQueryData(['invoices', id, 'full']) as any;
      if (detailData?.invoice) {
        queryClient.setQueriesData(
          {
            predicate: (query) => {
              const key = query.queryKey;
              if (key[0] !== 'invoices' || key.length < 2) return false;
              const secondKey = key[1];
              if (typeof secondKey === 'string' && secondKey.includes('-')) return false;
              if (secondKey === 'next-number') return false;
              return true;
            }
          },
          (old: any) => {
            if (!old?.invoices) return old;
            return {
              ...old,
              invoices: old.invoices.map((inv: any) =>
                inv.id === id
                  ? {
                      ...inv,
                      fee_amount: detailData.invoice.fee_amount,
                      tax_amount: detailData.invoice.tax_amount,
                      total_amount: detailData.invoice.total_amount,
                    }
                  : inv
              ),
            };
          }
        );
      }

      // Background invalidations — exclude detail query (already set via setQueryData)
      queryClient.invalidateQueries({
        queryKey: ['invoices'],
        predicate: (query) => !(query.queryKey.length === 3 && query.queryKey[2] === 'full'),
      });
      queryClient.invalidateQueries({ queryKey: ['invoice-items'] });
      invalidateProjectListQueries(queryClient);
      // R1 (SPEC-v017-completion-2026-09-27, AC5): removing a line UN-links
      // it from this invoice and returns the underlying billable item to
      // available ("Remove from invoice? The billable item will remain on
      // the project" — the confirmation dialog above already says so). Both
      // of AddItemModal's own write paths (add-existing, create-new)
      // invalidate ['billable-items', projectId] after every write; this
      // removal path never did, so the modal's "available items" list could
      // still show the item exactly as it was before removal (stale
      // remaining/invoiced amount) — her report: "deleted the entire thing
      // and re-entered, it still shows the same line item with an amount of
      // 1500 to be invoiced which is not the case."
      if (invoice?.project_id) {
        queryClient.invalidateQueries({ queryKey: ['billable-items', invoice.project_id] });
      }
    },
    // 2026-08-03: same gap as updateStatusMutation above — no onError meant the
    // generic global toast fired and the backend's reason (e.g. a locked/sent
    // invoice refusing item removal) never reached the user. Persistent toast
    // to match useUpdateInvoiceInline.
    onError: (err: any) => {
      mutationErrorToast(err, 'Failed to remove item from invoice');
    },
  });

  // Check for invoice error FIRST - don't wait for items if invoice doesn't exist
  // This fixes infinite loading when navigating to a non-existent invoice ID
  if (invoiceError) {
    // Distinguish between 404 (not found) and other errors (validation, network, etc.)
    const error = invoiceError as any;
    const is404 = error?.status === 404;
    const errorMessage = error?.message || 'An unexpected error occurred';

    return (
      <DashboardLayout title="Invoice">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600">
            {is404 ? 'Invoice not found' : 'Error loading invoice'}
          </h1>
          <p className="text-muted-foreground mt-2">
            {is404
              ? "The invoice you're looking for doesn't exist."
              : `Failed to load invoice: ${errorMessage}`
            }
          </p>
          {!is404 && (
            <p className="text-sm text-muted-foreground mt-1">
              Try refreshing the page. If the problem persists, contact support.
            </p>
          )}
          <Button onClick={() => navigate(getBackToListUrl())} className="mt-4">
            Back to Invoices
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  // Phase 9.19e: Single loading check - all data comes from combined endpoint
  if (invoiceLoading) {
    return (
      <DashboardLayout title="Invoice">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!invoice) {
    return (
      <DashboardLayout title="Invoice">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600">Invoice not found</h1>
          <p className="text-muted-foreground mt-2">
            The invoice you're looking for doesn't exist.
          </p>
          <Button onClick={() => navigate(getBackToListUrl())} className="mt-4">
            Back to Invoices
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  // Cancel-invoice feature: cancellable from any non-terminal status without
  // an active payment (mirrors backend cancel_invoice's own checks).
  const isCancelled = invoice.status === 'cancelled';
  const hasActivePayment = !!invoice.amount_paid && invoice.amount_paid > 0;
  // Christa-lens redirect (2026-09-15): shared with InvoiceListPage's row
  // Delete button so both surfaces stay in sync -- see lib/invoice-delete.ts.
  // Codex re-review, item 4: this page already loads the full payments list
  // (active + voided) for the Void Payment UI below -- pass it through so a
  // draft/ready invoice with payment history (the voided-back-to-draft bug)
  // gets the real backend wording instead of a bare "This action cannot be
  // undone" surprise.
  const hasPaymentHistory = (paymentsData?.payments?.length ?? 0) > 0;
  const deleteBlockedReason = getDeleteBlockedReason(invoice.status, hasPaymentHistory);
  // v0.14 X1 (2026-05-29): mirror backend invoice_not_editable_for_items guard.
  // Line-item changes (add / edit / remove) are only allowed in draft and ready
  // statuses — backend returns 409 invoice_not_editable_for_items on sent /
  // paid / partially_paid / cancelled. Hide the affordances to match.
  const isInvoiceItemsEditable = invoice.status === 'draft' || invoice.status === 'ready';
  // Phase 10b.2: Check if any expense items have attached receipts
  const hasExpenseReceipts = receiptCounts
    ? Object.values(receiptCounts).some(count => count > 0)
    : false;

  return (
    <DashboardLayout
      title={`Invoice #${invoice.invoice_number}`}
      actions={
        <>
          {/* Back button */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(getBackToListUrl())}
            data-testid="back-to-invoices"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Invoices
          </Button>
          {/* Back to project */}
          {invoice.project_id && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/projects/${invoice.project_id}?section=invoices`)}
              data-testid="back-to-project"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Project
            </Button>
          )}
          {/* State-driven primary CTA */}
          {(invoice.status === 'draft' || invoice.status === 'ready') && (
            <SendInvoiceButton
              invoiceId={invoice.id}
              invoiceNumber={invoice.invoice_number}
              projectName={invoice.project_name}
              defaultRecipient={invoice.client_contact_email || ''}
              accountingContacts={resolvedAccountingContacts}
              invoiceContacts={invoiceContacts}
              hasExpenseReceipts={hasExpenseReceipts}
              invoiceStatus={invoice.status as InvoiceStatus}
            />
          )}
          {(invoice.status === 'sent' || invoice.status === 'partially_paid') && (
            <Button
              variant="default"
              size="sm"
              onClick={() => setPaymentModalOpen(true)}
              data-testid="record-payment-button-header"
            >
              Record Payment
            </Button>
          )}
          {/* R3: Schedule follow-up — visible when invoice is unpaid/partial/overdue */}
          {['sent', 'partially_paid', 'overdue'].includes(invoice.status) && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setScheduleFollowupOpen(true)}
              data-testid="schedule-followup-button"
            >
              Schedule Follow-up
            </Button>
          )}
          {/* v0.12.21 Item C: Preview PDF promoted from kebab to top-line button.
              Christa didn't realize it was in the overflow menu. Same handler. */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setSendDialogInitialTab('pdf');
              setSendDialogPreviewOnly(true);
              setSendDialogOpen(true);
            }}
            data-testid="preview-pdf-button"
          >
            <Eye className="w-4 h-4 mr-1.5" />
            Preview PDF
          </Button>
          {/* Download PDF - always visible. R4 AC6: gated when identity is
              unresolved (needs a client) — the PDF is an entry point Christa
              actually uses to push an invoice out. R1's server gate also blocks
              the PDF path; this is the visible layer over it. */}
          <InvoicePDFDownload
            invoiceId={invoice.id}
            invoiceNumber={invoice.invoice_number}
            establishmentName={invoice.establishment_name}
            clientName={invoice.client_name}
            clientLegalName={invoice.client_legal_name}
            hideDba={invoice.hide_dba}
            disabled={identityGateBlocked}
            disabledReason={
              identityGateBlocked
                ? 'This invoice needs a client before you can download the PDF.'
                : undefined
            }
            onDownloaded={() => {
              void queryClient.invalidateQueries({ queryKey: ['invoices', id, 'document-history'] });
            }}
          />
          {/* Overflow menu - remaining actions */}
          <EntityActionMenu
            items={[
              // Draft: Mark as Ready
              // Theme 5b: disable + explain when the backend would 400 (no items /
              // sum-mismatch) instead of firing a doomed mutation.
              ...(invoice.status === 'draft' ? [{
                label: 'Mark as Ready',
                icon: CheckCircle,
                onClick: () => {
                  if (readySentBlocked) return;
                  updateStatusMutation.mutate({ status: 'ready' });
                },
                disabled: updateStatusMutation.isPending || !!readySentBlocked,
                title: readySentBlocked,
              }] : []),
              // Ready: Mark as Sent
              ...(invoice.status === 'ready' ? [{
                label: 'Mark as Sent',
                icon: Send,
                onClick: () => {
                  if (readySentBlocked) return;
                  setMarkSentRevisionId('');
                  setMarkSentOpen(true);
                },
                disabled: updateStatusMutation.isPending || !!readySentBlocked,
                title: readySentBlocked,
              }] : []),
              // Sent/Paid/Partially Paid: Resend Invoice
              ...(invoice.status === 'sent' || invoice.status === 'paid' || invoice.status === 'partially_paid' ? [{
                label: 'Resend Invoice',
                icon: Mail,
                onClick: () => {
                  setSendDialogInitialTab('edit');
                  setSendDialogPreviewOnly(false);
                  setSendDialogOpen(true);
                },
              }] : []),
              // R4 AC1: Review merge, offered in every lifecycle state (the server plan decides what is allowed).
              // A merged-away invoice offers "Open current invoice" in its banner instead.
              ...(reviewedMerge && canDeleteInvoices && !invoice.merged_into_invoice_id ? [{
                label: 'Review merge…',
                icon: Combine,
                onClick: () => setShowMergeReview(true),
                separator: true,
              }] : []),
              // Void Payment — show whenever amount_paid > 0 (supports partial payments on 'sent' invoices too)
              ...(invoice.amount_paid && invoice.amount_paid > 0 ? [{
                label: 'Void Payment',
                icon: Ban,
                onClick: () => {
                  // V172: open new reason-based void dialog instead of old amount dialog
                  setVoidReason('');
                  setSelectedPaymentId(undefined);
                  setShowVoidPaymentDialog(true);
                },
                disabled: voidInvoicePaymentMutation.isPending,
                variant: 'destructive' as const,
                separator: true,
              }] : []),
              // Cancel Invoice — cancel-invoice feature. Hidden entirely
              // without can_delete_invoices, or once already cancelled
              // (terminal); disabled with an explanatory title when an
              // active payment exists (mirrors the backend's "Void the
              // payment first" refusal).
              ...(canDeleteInvoices && !isCancelled ? [{
                label: 'Cancel Invoice',
                icon: XCircle,
                onClick: () => setShowCancelDialog(true),
                disabled: hasActivePayment,
                title: hasActivePayment ? 'Void the payment first.' : undefined,
                variant: 'destructive' as const,
                separator: true,
              }] : []),
              // Delete — hidden entirely without can_delete_invoices.
              // Disabled (with a title explaining why) for every status the
              // backend actually refuses, so she never has to open the
              // confirmation just to be told to cancel instead.
              ...(canDeleteInvoices ? [{
                label: 'Delete',
                icon: Trash2,
                onClick: () => setShowDeleteConfirm(true),
                variant: 'destructive' as const,
                disabled: !!deleteBlockedReason,
                title: deleteBlockedReason,
                separator: true,
              }] : []),
            ] as ActionMenuItem[]}
          />
          {/* Hidden send dialog for resend/history from overflow menu and EmailSendIndicator */}
          <SendInvoiceButton
            invoiceId={invoice.id}
            invoiceNumber={invoice.invoice_number}
            projectName={invoice.project_name}
            defaultRecipient={invoice.client_contact_email || ''}
            accountingContacts={resolvedAccountingContacts}
            invoiceContacts={invoiceContacts}
            hideButton
            sendEvidence={invoice.send_evidence ?? null}
            open={sendDialogOpen}
            onOpenChange={setSendDialogOpen}
            initialTab={sendDialogInitialTab}
            previewOnly={sendDialogPreviewOnly}
            hasExpenseReceipts={hasExpenseReceipts}
            invoiceStatus={invoice.status as InvoiceStatus}
          />
        </>
      }
    >
      <UnconfirmedSendBanner
        invoiceId={invoice.id}
        clientName={invoice.client_name}
        canEdit={canEditInvoices}
      />
      {/* SPEC-invoice-merge-history R3: an invoice that was merged away says so at the top, with the
          current invoice one click away. Its own history and saved PDFs stay below. */}
      {invoice.merged_into_invoice_id && (
        <div role="status" className="mb-6 rounded border border-blue-300 bg-blue-50 p-3 text-sm" data-testid="invoice-merged-banner">
          <p>
            Invoice {invoice.invoice_number} was merged into invoice{' '}
            {invoice.merged_into_invoice_number ?? 'another invoice'}. It no longer holds charges or payments; its original
            history and saved PDFs stay available below.
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={() => navigate(`/invoices/${invoice.merged_into_invoice_id}`)}
            data-testid="invoice-merged-banner-open-current"
          >
            Open current invoice {invoice.merged_into_invoice_number ?? ''}
          </Button>
        </div>
      )}

      {/* SPEC-invoice-merge-history R4 AC5: the combined invoice shows whether THIS version was ever sent
          separately from whether it is paid. Paid is not proof of send, and a send of an earlier invoice
          is history, not a send of this version. */}
      {hasMergeHistory && !invoice.merged_into_invoice_id && (() => {
        const paid = Number(invoice.amount_paid || 0);
        const total = Number(invoice.total_amount || 0);
        const version = documentHistoryQuery.data?.current_version;
        const issuance =
          version?.state === 'sent' ? 'A send is recorded for this version.'
            : version?.state === 'send_outcome_unknown' ? 'A send of this version has an unconfirmed outcome.'
              : contentChangedAfterSend(version) ? changedAfterSendLine(invoice.status)
                : 'This version has not been sent yet.';
        const payment = paid > total
          ? `Overpaid: ${formatMergeMoney((paid - total).toFixed(2))} held on this invoice (nothing refunded or transferred)`
          : total > 0 && paid >= total ? 'Paid in full'
            : paid > 0 ? `Partially paid (${formatMergeMoney(paid.toFixed(2))} of ${formatMergeMoney(total.toFixed(2))})`
              : 'No payment received';
        return (
          <div className="mb-6 rounded border p-3 text-sm" data-testid="invoice-combined-state">
            <p className="font-medium">Combined invoice</p>
            <p data-testid="invoice-combined-issuance">Issuance: {issuance}</p>
            <p data-testid="invoice-combined-payment">Payment: {payment}</p>
          </div>
        );
      })()}

      {/* Phase 9.18b: Invoice Header - matches PDF structure */}
      <div className="mb-8">
        {/* 9.18j: Compact header row - status and payment info together */}
        <div className="flex flex-wrap items-center gap-6 mb-6">
          <div>
            {/* 9.18i: Add clear label for status */}
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Invoice Status</p>
            {/* Phase 6 Batch 5.3: Inline invoice status editing */}
            {isEditingStatus ? (
              <InvoiceStatusSelect
                value={invoice.status as InvoiceStatus}
                onChange={(newStatus) => {
                  // Super-admin destructive-op guard: a super-admin downgrading
                  // status (paid→sent / sent→ready / ready→draft / any→cancelled)
                  // on production data must confirm first — the confirm step then
                  // sends the per-invoice X-Super-Admin-Confirm-Destructive header
                  // so the backend guard lets it through instead of 403ing. This
                  // is also what makes the previously-shown-but-dead Draft option
                  // actually work for super-admins. Non-super-admins and
                  // upgrades fall straight through to the existing behaviour.
                  if (isSuperAdmin && isGuardedStatusDowngrade(invoice.status, newStatus)) {
                    setPendingDowngradeStatus(newStatus);
                    setIsEditingStatus(false);
                    return;
                  }
                  if (newStatus === 'sent' && invoice.status === 'ready') {
                    setMarkSentRevisionId('');
                    setMarkSentOpen(true);
                    setIsEditingStatus(false);
                    return;
                  }
                  updateInvoiceInline.mutate({
                    id: id!,
                    data: { status: newStatus }
                  });
                  setIsEditingStatus(false);
                }}
                onCancel={() => setIsEditingStatus(false)}
                disabled={updateInvoiceInline.isPending}
                className="w-[180px]"
                amountPaid={invoice.amount_paid || 0}
                // Theme 5b: gate Mark Ready/Sent client-side (backend 400s these)
                lineItemCount={items.length}
                invoiceTotal={Number(invoice.total_amount) || 0}
                lineItemsSum={lineItemsSum}
              />
            ) : (
              <div
                onClick={() => setIsEditingStatus(true)}
                title="Click to change invoice status"
                data-testid="invoice-status-badge-clickable"
                className="cursor-pointer"
              >
                <InvoiceStatusBadge
                  status={invoice.status as InvoiceStatus}
                  editable={true}
                  amountPaid={invoice.amount_paid || 0}
                  totalAmount={invoice.total_amount}
                />
              </div>
            )}
          </div>
          {/* Record Payment lives once, in the header action bar (record-payment-button-header); the status-row
              copy was an identical duplicate (same statuses, same modal) and was removed. */}
          {/* 9.18j: Payment Info with consistent field labels */}
          {(invoice.status === 'sent' || invoice.status === 'paid' || invoice.status === 'partially_paid') && (
            <>
              {/* Amount Paid with Progress Bar */}
              <div className="min-w-[200px]">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Amount Paid</p>
                <p className="text-sm font-medium mb-1">
                  {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(invoice.amount_paid || 0)}
                  <span className="text-muted-foreground font-normal">
                    {' / '}
                    {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(invoice.total_amount)}
                  </span>
                  <span className="text-muted-foreground ml-1">
                    ({invoice.total_amount > 0 ? Math.min(100, Math.round(((invoice.amount_paid || 0) / invoice.total_amount) * 100)) : 0}%)
                  </span>
                </p>
                {/* R4 AC5: money received above the total is shown as an overpayment held, never a percentage over 100. */}
                {(invoice.amount_paid || 0) > invoice.total_amount && (
                  <p className="text-xs text-amber-700 mb-1" data-testid="payment-excess-held">
                    Excess received: {formatMergeMoney(((invoice.amount_paid || 0) - invoice.total_amount).toFixed(2))} (held on this invoice; nothing refunded)
                  </p>
                )}
                {/* Progress bar */}
                <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all duration-300 ${
                      (invoice.amount_paid || 0) >= invoice.total_amount
                        ? 'bg-green-500'
                        : (invoice.amount_paid || 0) > 0
                          ? 'bg-blue-500'
                          : 'bg-gray-300'
                    }`}
                    style={{
                      width: `${invoice.total_amount > 0 ? Math.min(100, ((invoice.amount_paid || 0) / invoice.total_amount) * 100) : 0}%`
                    }}
                    data-testid="payment-progress-bar"
                  />
                </div>
              </div>

              {/* Payment Date */}
              {invoice.payment_date && (invoice.amount_paid || 0) > 0 && (
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                    {(invoice.amount_paid || 0) >= invoice.total_amount ? 'Paid On' : 'Last Payment'}
                  </p>
                  <p className="text-sm" data-testid="payment-date-inline">
                    {/* v0.14 (Dmitri sweep): payment_date is a date-only string (YYYY-MM-DD).
                        new Date('YYYY-MM-DD') parses as UTC midnight and shifts one
                        day back in negative-UTC tz (MT = UTC-7), so the side card
                        showed "May 28" when the row read "May 29". Use formatDateOnly
                        (local-time constructor) to keep the calendar date stable. */}
                    {formatDateOnly(invoice.payment_date, { month: 'short', day: 'numeric', year: 'numeric' })}
                  </p>
                </div>
              )}

              {/* Payment Status */}
              {(invoice.amount_paid || 0) >= invoice.total_amount && invoice.total_amount > 0 && (
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Payment Status</p>
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                    Fully Paid
                  </span>
                </div>
              )}
            </>
          )}
        </div>

        {/* R2 (SPEC-v017-completion-2026-09-27): "who do I send this invoice
            to?" — 2026-09-21, inv 0729: "I'm finding that I need to go back
            through my emails to see who to send these too... all the ones
            today". She downloads the PDF and sends from Outlook, so the answer
            sits ABOVE the two-column body, in the first screen, next to the
            Download button — not inside the Send modal she never opens. */}
        <InvoiceRecipientsCard
          lookup={recipientLookup}
          clientName={invoice.client_name ?? null}
          /* QA round 2 (MED-1): read the SAME rule BILL TO uses to decide
             whether its Add-recipient trigger renders, so the card can never
             offer a link to an affordance that is not there. If that rule ever
             gains a status lock again, this follows it automatically. */
          canAddRecipient={isBillToEditable(
            invoice.status as Parameters<typeof isBillToEditable>[0],
          )}
          onAddRecipient={focusAddRecipient}
        />

        {/* Two-column layout matching PDF structure */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* LEFT COLUMN: Invoice For (Client) */}
          <div className="space-y-4">
            {/* R4 (v0.14): consolidated BILL TO panel — Variant A document-style.
                Replaces the v0.12-era three-dot-menu / hover-pencil card.
                See BillToPanel.tsx + R4-bill-to-mockups-2026-05-25.md. */}
            <BillToPanel
              fieldProvenance={documentHistoryQuery.data?.current_field_provenance}
              invoice={{
                id: invoice.id,
                // R3 (invoice-identity-correctable): drives the binary
                // persistence-choice consequence copy ("Fix only invoice 0602").
                invoice_number: invoice.invoice_number ?? null,
                organization_id: invoice.organization_id ?? null,
                project_id: invoice.project_id ?? null,
                // R1 AC10: the add-recipient modal names the project the way she reads it, "A-197 Hotel Renovation".
                project_number: invoice.project_number ?? null,
                project_name: invoice.project_name ?? null,
                project_address: invoice.project_address ?? null,
                client_scope_id: invoice.client_scope_id ?? null,
                client_name: invoice.client_name ?? null,
                client_legal_name: invoice.client_legal_name ?? null,
                identity_effective: invoice.identity_effective,
                establishment_id: invoice.establishment_id ?? null,
                establishment_name: invoice.establishment_name ?? null,
                establishment_legal_name: invoice.establishment_legal_name ?? null,
                establishment_address: invoice.establishment_address ?? null,
                hide_dba: invoice.hide_dba ?? false,
                billing_address_source: invoice.billing_address_source ?? null,
                billing_address_override: invoice.billing_address_override ?? null,
                frozen_billing_address: invoice.frozen_billing_address ?? null,
                org_billing_address: invoice.org_billing_address ?? null,
                org_address: invoice.org_address ?? null,
                // Hotfix2 — Bug #3: drives BILL TO editability gating.
                status: invoice.status ?? null,
                send_evidence: invoice.send_evidence ?? null,
              }}
              establishments={establishmentsData}
              recipientsMeta={invoiceData?.recipients_meta ?? []}
              scopeState={invoiceData?.scope_state ?? null}
              addressState={invoiceData?.address_state ?? null}
              legalNameState={invoiceData?.legal_name_state ?? null}
              toContactsMeta={toContactsMeta}
              // R4 (invoice-identity-cascade): the Christa-Land cascade surface.
              // `identity_resolution` is R2's resolution bundle on the GET-full
              // payload; it ships once the operator/R5 surfaces it on the read
              // path (R2 built the engine + this consumes it). Optional so the
              // panel renders without it; when present it drives the source
              // tags, the consequence-preview picker, and the gate.
              resolution={resolutionForPanel}
              projectDraftCount={resolutionForPanel?.project_draft_count ?? 0}
              clientInvoiceCount={resolutionForPanel?.client_invoice_count ?? 0}
              multiClientProject={resolutionForPanel?.multi_client_project ?? false}
              lastSentNumber={resolutionForPanel?.last_sent_number ?? null}
              onGateStateChange={setIdentityGateBlocked}
            />
          </div>

          {/* RIGHT COLUMN: Invoice Details */}
          <div className="space-y-4">
            {/* 9.31b: Project moved to top of right column (was in left column) */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Project</p>
              <div className="bg-blue-50 border border-blue-100 rounded p-3 flex items-center gap-2">
                <p className="text-sm font-medium">{invoice.project_name}</p>
                {/* v0.12.27 Item 8: real anchor so Cmd/Ctrl+click, middle-click,
                    and "Copy link" all behave like normal browser links. */}
                <Link
                  to={`/projects/${invoice.project_id}?section=invoices`}
                  className="p-1 hover:bg-blue-100 rounded transition-colors inline-flex"
                  title="View project details"
                  aria-label="View project details"
                  data-testid="invoice-billto-project-link"
                >
                  <ExternalLink className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
                </Link>
              </div>
            </div>

            {/* Invoice Number - 9.18j: editable for drafts */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Invoice Number</p>
              <div
                className={`bg-blue-50 border border-blue-100 rounded p-3 ${invoice.status === 'draft' ? 'hover:bg-blue-100 transition-colors cursor-pointer' : ''}`}
                onClick={() => {
                  if (invoice.status === 'draft' && !isEditingInvoiceNumber) {
                    setEditedInvoiceNumber(invoice.invoice_number);
                    setIsEditingInvoiceNumber(true);
                  }
                }}
              >
                {isEditingInvoiceNumber ? (
                  <Input
                    value={editedInvoiceNumber}
                    onChange={(e) => setEditedInvoiceNumber(e.target.value)}
                    onBlur={() => {
                      if (editedInvoiceNumber && editedInvoiceNumber !== invoice.invoice_number) {
                        updateInvoiceInline.mutate({
                          id: id!,
                          data: { invoice_number: editedInvoiceNumber }
                        });
                      }
                      setIsEditingInvoiceNumber(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        e.currentTarget.blur();
                      } else if (e.key === 'Escape') {
                        setEditedInvoiceNumber(invoice.invoice_number);
                        setIsEditingInvoiceNumber(false);
                      }
                    }}
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                    className="text-base font-semibold"
                    data-testid="invoice-number-input"
                  />
                ) : (
                  <p
                    className={`text-base font-semibold ${invoice.status === 'draft' ? 'flex items-center gap-2' : ''}`}
                    data-testid="invoice-number-display"
                  >
                    {invoice.invoice_number}
                    {invoice.status === 'draft' && <Pencil className="h-3 w-3 text-muted-foreground" />}
                  </p>
                )}
              </div>
            </div>

            {/* Invoice Date - editable for drafts */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Invoice Date</p>
              <div
                className={`bg-blue-50 border border-blue-100 rounded p-3 ${invoice.status === 'draft' ? 'hover:bg-blue-100 transition-colors cursor-pointer' : ''}`}
                onClick={() => {
                  if (invoice.status === 'draft' && !isEditingDate) {
                    setEditedDate(invoice.invoice_date);
                    setIsEditingDate(true);
                  }
                }}
              >
                {isEditingDate ? (
                  <input
                    type="date"
                    value={editedDate}
                    onChange={(e) => setEditedDate(e.target.value)}
                    onBlur={() => {
                      if (editedDate && editedDate !== invoice.invoice_date) {
                        updateInvoiceInline.mutate({
                          id: id!,
                          data: { invoice_date: editedDate }
                        });
                      }
                      setIsEditingDate(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.currentTarget.blur();
                      } else if (e.key === 'Escape') {
                        setIsEditingDate(false);
                      }
                    }}
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                    className="w-full px-2 py-1 border border-blue-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-transparent bg-white"
                    data-testid="invoice-date-input"
                  />
                ) : (
                  <p
                    className={`text-sm ${invoice.status === 'draft' ? 'flex items-center gap-2' : ''}`}
                    data-testid="invoice-date-display"
                  >
                    {invoice.invoice_date}
                    {invoice.status === 'draft' && <Pencil className="h-3 w-3 text-muted-foreground" />}
                  </p>
                )}
              </div>
              {/* Christa feedback 2026-04-08: remind to back-date when billing for prior month */}
              {shouldShowDateWarning && (
                <div
                  data-testid="invoice-date-late-warning"
                  className="text-amber-600 text-xs mt-1 flex items-start gap-1"
                >
                  <span aria-hidden>⚠</span>
                  <span>
                    This invoice is dated more than 2 weeks after the newest line item
                    ({newestLineItemMonthLabel}). Did you mean to date it for the previous month?
                  </span>
                </div>
              )}
            </div>

            {/* Due Date - 9.18j: editable for drafts */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Due Date</p>
              <div
                className={`bg-blue-50 border border-blue-100 rounded p-3 ${invoice.status === 'draft' ? 'hover:bg-blue-100 transition-colors cursor-pointer' : ''}`}
                onClick={() => {
                  if (invoice.status === 'draft' && !isEditingDueDate) {
                    setEditedDueDate(invoice.due_date);
                    setIsEditingDueDate(true);
                  }
                }}
              >
                {isEditingDueDate ? (
                  <input
                    type="date"
                    value={editedDueDate}
                    onChange={(e) => setEditedDueDate(e.target.value)}
                    onBlur={() => {
                      if (editedDueDate && editedDueDate !== invoice.due_date) {
                        updateInvoiceInline.mutate({
                          id: id!,
                          data: { due_date: editedDueDate }
                        });
                      }
                      setIsEditingDueDate(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.currentTarget.blur();
                      } else if (e.key === 'Escape') {
                        setIsEditingDueDate(false);
                      }
                    }}
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                    className="w-full px-2 py-1 border border-blue-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-transparent bg-white"
                    data-testid="due-date-input"
                  />
                ) : (
                  <p
                    className={`text-sm ${invoice.status === 'draft' ? 'flex items-center gap-2' : ''}`}
                    data-testid="due-date-display"
                  >
                    {invoice.due_date}
                    {invoice.status === 'draft' && <Pencil className="h-3 w-3 text-muted-foreground" />}
                  </p>
                )}
              </div>
            </div>

            {/* PO Number - SPEC-project-po-number-2026-08-17 R2. Near the
                invoice number/dates so it reads as part of the invoice's own
                identity, matching where R3 prints it on the PDF. Editable on
                ANY status (see InvoicePoNumberField's own docstring). */}
            <InvoicePoNumberField invoice={invoice} />

            {/* Email Send History - inline indicator */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Email</p>
              <EmailSendIndicator
                entityType="invoice"
                entityId={invoice.id}
                sendEvidence={invoice.send_evidence ?? null}
                invoiceStatus={invoice.status}
                statusChangedAt={invoice.status_changed_at ?? null}
                contentChangedAfterSend={contentChangedAfterSend(documentHistoryQuery.data?.current_version)}
                onViewHistory={() => {
                  setSendDialogInitialTab('history');
                  setSendDialogPreviewOnly(false);
                  setSendDialogOpen(true);
                }}
              />
            </div>

            {/* Dropbox Sync Status */}
            {(invoice as any).dropbox_synced_at !== undefined && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Dropbox</p>
                <DropboxSyncIndicator
                  dropboxSyncedAt={(invoice as any).dropbox_synced_at}
                  dropboxPath={(invoice as any).dropbox_path}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Phase 9.18b: Line Items grouped by category (Services vs Expenses) */}
      {(() => {
        // Service categories: professional_fee, add_service, renewal
        const serviceCategories = ['professional_fee', 'add_service', 'renewal'];
        // Expense categories: billable_expense, pass_through
        const expenseCategories = ['billable_expense', 'pass_through'];

        // V234 (QA round 2 on SPEC-v017-completion-2026-09-27 R4): group by
        // the LINE's category, falling back to the billable item's when the
        // line has none. The backend already resolves that fallback into
        // `line_category`; reading the stored item's category here made the
        // screen contradict the rendered PDF for exactly the case R4 shipped
        // -- an app fee she billed as an expense on this one invoice.
        const lineCategory = (item: typeof items[number]) =>
          item.line_category || item.billable_item_category || '';

        const serviceItems = items.filter(item =>
          serviceCategories.includes(lineCategory(item))
        );
        const expenseItems = items.filter(item =>
          expenseCategories.includes(lineCategory(item))
        );
        // Items without category (legacy data) go to services
        const uncategorizedItems = items.filter(item =>
          !lineCategory(item) ||
          (!serviceCategories.includes(lineCategory(item)) &&
           !expenseCategories.includes(lineCategory(item)))
        );
        const allServiceItems = [...serviceItems, ...uncategorizedItems];

        // Calculate subtotals
        const serviceSubtotal = allServiceItems.reduce((sum, item) => sum + item.amount, 0);
        const serviceTax = allServiceItems.reduce((sum, item) => sum + item.tax_amount, 0);
        const expenseSubtotal = expenseItems.reduce((sum, item) => sum + item.amount, 0);

        // R2 (SPEC-bc-pst-tax-engine-2026-08-04, AC7/AC8): one totals row per
        // DISTINCT TAX, read from the per-line `taxes` detail (attached by the
        // backend from invoice_item_taxes / legacy derivation). No longer the
        // first line's tax_rate with a literal-5 fallback.
        // R3 AC1/AC2: decided per line (lib/invoiceTaxRows.ts), so a line added without per-tax detail is never
        // dropped from the GST row.
        const serviceTaxRows = computeServiceTaxRows(allServiceItems);

        return (
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Line Items</h2>
              {isInvoiceItemsEditable && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setAddItemModalOpen(true)}
                  data-testid="add-item-button"
                >
                  Add Item
                </Button>
              )}
            </div>

            {/* Services Section */}
            {allServiceItems.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-2">
                  Services
                </h3>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-right">Tax Rate</TableHead>
                      <TableHead>
                        <span className="inline-flex items-center gap-1">
                          Tax class
                          <TaxClassInfoTooltip context={taxEngineContext} testId="tax-class-info-icon-services" />
                        </span>
                      </TableHead>
                      <TableHead className="text-right">Tax</TableHead>
                      {/* R4 (christa-reviewer, iteration 3): InvoiceItemRow now
                          always renders a cell in this slot (edit/remove
                          controls when editable, an explanatory message when
                          locked) — the header column must always render too
                          or the row/header column counts drift. */}
                      <TableHead className="w-24"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allServiceItems.map((item) => (
                      <InvoiceItemRow
                        key={item.id}
                        item={item}
                        invoiceId={id!}
                        invoiceStatus={invoice.status as 'draft' | 'ready' | 'sent' | 'paid' | 'cancelled' | 'overdue'}
                        invoiceSendRecorded={isSent(invoice.send_evidence)}
                        onRemove={(itemId) => setRemoveItemId(itemId)}
                      />
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Expenses Section */}
            {expenseItems.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-2">
                  Expenses
                </h3>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-right">Tax Rate</TableHead>
                      <TableHead>
                        <span className="inline-flex items-center gap-1">
                          Tax class
                          <TaxClassInfoTooltip context={taxEngineContext} testId="tax-class-info-icon-expenses" />
                        </span>
                      </TableHead>
                      <TableHead className="text-right">Tax</TableHead>
                      {/* R4 (christa-reviewer, iteration 3): see matching
                          comment in the Services table header above. */}
                      <TableHead className="w-24"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {expenseItems.map((item) => (
                      <InvoiceItemRow
                        key={item.id}
                        item={{
                          ...item,
                          // Phase 10b.2: Attach receipt count for indicator icon
                          receipt_count: item.billable_item_id && receiptCounts
                            ? receiptCounts[item.billable_item_id] ?? 0
                            : 0,
                        }}
                        invoiceId={id!}
                        invoiceStatus={invoice.status as 'draft' | 'ready' | 'sent' | 'paid' | 'cancelled' | 'overdue'}
                        invoiceSendRecorded={isSent(invoice.send_evidence)}
                        onRemove={(itemId) => setRemoveItemId(itemId)}
                      />
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Phase 9.19: Enhanced empty state for Quick Create flow */}
            {items.length === 0 && (
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center" data-testid="empty-items-state">
                <Package className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No items yet</h3>
                <p className="text-gray-500 mb-4">
                  {isInvoiceItemsEditable
                    ? 'Add billable items to this invoice before sending'
                    : 'This invoice has no line items'}
                </p>
                {isInvoiceItemsEditable && (
                  <Button onClick={() => setAddItemModalOpen(true)} data-testid="empty-state-add-items">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Items
                  </Button>
                )}
              </div>
            )}

            {/* Invoice Summary - matching PDF structure */}
            <div className="flex justify-end mt-6">
              <div className="w-72 space-y-2" data-testid="invoice-totals">
                {/* Service subtotal */}
                {allServiceItems.length > 0 && (
                  <>
                    <div className="flex justify-between items-center text-sm">
                      <span>Service Subtotal</span>
                      <span>
                        ${serviceSubtotal.toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </div>
                    {serviceTax > 0 && serviceTaxRows.map((row) => (
                      <div
                        key={`${row.code}-${row.rate ?? 'x'}`}
                        className="flex justify-between items-center text-sm"
                        data-testid={`invoice-tax-row-${row.code}`}
                      >
                        <span>
                          {`${row.label}${row.rate !== null ? ` (${row.rate}%)` : ''}${
                            row.baseFraction !== null && row.baseFraction < 1
                              ? ` on ${Math.round(row.baseFraction * 100)}% of base`
                              : ''
                          }`}
                        </span>
                        <span className="font-medium">
                          ${row.amount.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </div>
                    ))}
                  </>
                )}
                {/* Expense subtotal */}
                {expenseItems.length > 0 && (
                  <div className="flex justify-between items-center text-sm">
                    <span>Expense Subtotal</span>
                    <span>
                      ${expenseSubtotal.toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                )}
                {/* Grand Total */}
                <div className="flex justify-between items-center border-t-2 border-gray-900 pt-2 mt-2">
                  <span className="text-lg font-bold">Total</span>
                  <span className="text-lg font-bold">
                    ${invoice.total_amount.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Phase 10b.2: Expense Receipts Section */}
      {receiptCounts && (
        <InvoiceReceiptsSection
          expenseItems={items
            .filter(item => ['billable_expense', 'pass_through'].includes(item.line_category || item.billable_item_category || ''))
            .map(item => ({
              id: item.id,
              billable_item_id: item.billable_item_id,
              description: item.description,
              billable_item_description: item.billable_item_description,
              billable_item_category: item.billable_item_category,
            }))}
          receiptCounts={receiptCounts}
        />
      )}

      {/* Phase 9.18h: Invoice Notes Section (moved to bottom to match PDF structure) */}
      <div className="mt-8">
        <InvoiceNotesSection invoice={invoice} />
      </div>

      {/* R3: Collection follow-ups section */}
      {['sent', 'partially_paid', 'overdue', 'paid'].includes(invoice.status) && (
        <div className="mt-6 border-t pt-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Collection Follow-ups</h3>
          <FollowupsSection invoiceId={invoice.id} />
          {['sent', 'partially_paid', 'overdue'].includes(invoice.status) && (
            <button
              type="button"
              onClick={() => setScheduleFollowupOpen(true)}
              className="mt-3 text-sm text-blue-600 hover:text-blue-800 hover:underline"
              data-testid="add-followup-link"
            >
              + Schedule a follow-up
            </button>
          )}
        </div>
      )}

      {/* V172: Payment History ledger */}
      <InvoicePaymentHistory invoiceId={invoice.id} />

      <div className="mt-8">
        <InvoiceDocumentHistory
          data={documentHistoryQuery.data ?? null}
          invoiceStatus={invoice.status}
          loading={documentHistoryQuery.isLoading}
          error={documentHistoryQuery.error instanceof Error ? documentHistoryQuery.error.message : null}
          busy={documentActionBusy}
          lineageEvents={lineageEvents}
          lineageHasMore={lineageQuery.hasNextPage === true}
          lineageLoading={lineageQuery.isFetching}
          lineageError={lineageQuery.error instanceof Error ? lineageQuery.error.message : null}
          onLoadMoreLineage={() => { void lineageQuery.fetchNextPage(); }}
          onOpenInvoice={(invoiceId) => navigate(`/invoices/${invoiceId}`)}
          unavailableRevisionIds={unavailableRevisionIds}
          onDownloadRevision={async (revisionId, originalInvoiceId, filenameSuffix) => {
            setDocumentActionBusy(true);
            try {
              // A copy that belongs to an invoice merged into this one is opened under ITS invoice,
              // so the archived original bytes and hash come back.
              const { blob, filename } = await api.invoices.downloadRevision(originalInvoiceId ?? invoice.id, revisionId);
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              const baseName = filename ?? `invoice-${invoice.invoice_number}.pdf`;
              // In a merged lineage the saved copies of one invoice share a name; the suffix tells them apart
              // (ORIGINAL before combining / COMBINED current version) without touching the stored bytes.
              link.download = filenameSuffix
                ? baseName.replace(/(\.pdf)?$/i, ` - ${filenameSuffix}.pdf`)
                : baseName;
              document.body.appendChild(link);
              link.click();
              link.remove();
              URL.revokeObjectURL(url);
            } catch (error) {
              if (error instanceof DocumentBytesUnavailableError) {
                setUnavailableRevisionIds((previous) => new Set(previous).add(revisionId));
              } else {
                mutationErrorToast(error, 'Could not download saved PDF');
              }
            } finally {
              setDocumentActionBusy(false);
            }
          }}
          onAssertExternalSend={async (revisionId) => {
            setDocumentActionBusy(true);
            try {
              if (invoice.status === 'draft' || invoice.status === 'ready') {
                // The existing status route writes Sent + manual external evidence
                // in one transaction. Its server guard still decides readiness.
                await updateStatusMutation.mutateAsync({ status: 'sent', revisionId });
              } else {
                // Already sent/paid: another assertion is supplemental evidence.
                await api.invoices.assertExternalSend(invoice.id, revisionId);
                await queryClient.invalidateQueries({ queryKey: ['invoices', id, 'document-history'] });
              }
            } catch (error) {
              if (invoice.status !== 'draft' && invoice.status !== 'ready') {
                mutationErrorToast(error, 'Could not record external send');
              }
            } finally {
              setDocumentActionBusy(false);
            }
          }}
        />
      </div>

      <Dialog open={markSentOpen} onOpenChange={setMarkSentOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark invoice as sent outside Keystone</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Choose the exact saved PDF if you know which copy was sent. Otherwise the copy will be recorded as unknown.
          </p>
          <label className="text-sm font-medium" htmlFor="mark-sent-revision">Saved PDF copy</label>
          <select
            id="mark-sent-revision"
            value={markSentRevisionId}
            onChange={(event) => setMarkSentRevisionId(event.currentTarget.value)}
            className="rounded border bg-background px-3 py-2"
          >
            <option value="">Unknown / not recorded</option>
            {(documentHistoryQuery.data?.revisions ?? []).map((revision) => (
              <option key={revision.id} value={revision.id}>
                {revision.filename} · {new Date(revision.created_at).toLocaleString()} · {revision.id.slice(0, 8)}
              </option>
            ))}
          </select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarkSentOpen(false)}>Cancel</Button>
            <Button
              disabled={updateStatusMutation.isPending}
              onClick={() => updateStatusMutation.mutate({ status: 'sent', revisionId: markSentRevisionId || null })}
            >
              {updateStatusMutation.isPending ? 'Saving…' : 'Mark as Sent'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit History */}
      {invoice.audit_history && invoice.audit_history.length > 0 && (
        <div className="mt-8">
          <AuditHistoryTimeline history={invoice.audit_history} />
        </div>
      )}

      {/* Payment Modal */}
      <PaymentModal
        open={paymentModalOpen}
        onOpenChange={setPaymentModalOpen}
        invoiceId={invoice.id}
        totalAmount={invoice.total_amount}
        amountPaid={invoice.amount_paid || 0}
        // 2026-08-03 reconciliation fix: name the invoice inside the modal, the
        // same way the void dialog names the payment it is about to void.
        invoiceNumber={invoice.invoice_number}
        clientName={invoice.client_legal_name || invoice.client_name}
        onSuccess={() => {
          // Phase 9.19e: Invalidate combined query
          queryClient.invalidateQueries({ queryKey: ['invoices', id, 'full'] });
        }}
      />

      {/* Add Item Modal */}
      <AddItemModal
        open={addItemModalOpen}
        onOpenChange={setAddItemModalOpen}
        invoiceId={invoice.id}
        projectId={invoice.project_id}
        existingItemIds={items.map(item => item.billable_item_id).filter((id): id is string => id !== null)}
        onSuccess={() => {
          // Cache updates handled by AddItemModal via setQueryData.
          // Background invalidations fire from AddItemModal for eventual consistency.
        }}
      />

      {/* V172: Void Payment Dialog — requires void_reason, payment picker when >1 active payment */}
      {(() => {
        const allPayments = paymentsData?.payments ?? [];
        const activePayments = allPayments.filter((p: any) => p.status === 'active');
        // Most recent active payment first
        const sortedActive = [...activePayments].sort(
          (a: any, b: any) => new Date(b.paid_at).getTime() - new Date(a.paid_at).getTime(),
        );
        const targetPaymentId = selectedPaymentId ?? sortedActive[0]?.id;
        const targetPayment = allPayments.find((p: any) => p.id === targetPaymentId);
        // Remaining active count after void
        const remainingActiveCount = activePayments.filter((p: any) => p.id !== targetPaymentId).length;
        const isMigratedPayment = targetPayment?.recorded_by === null;

        return (
          <Dialog
            open={showVoidPaymentDialog}
            onOpenChange={(open) => {
              setShowVoidPaymentDialog(open);
              if (!open) {
                setVoidReason('');
                setSelectedPaymentId(undefined);
              }
            }}
          >
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Void Payment</DialogTitle>
              </DialogHeader>

              <div className="space-y-4">
                {/* Super-admin destructive-op guard: surface that this void is a
                    guarded destructive action on production data. Confirming
                    sends the per-invoice override header so the backend allows
                    it. Only shown to super-admins. */}
                {isSuperAdmin && (
                  <div
                    className="rounded-md bg-red-50 border border-red-200 px-3 py-2.5 text-sm text-red-800"
                    data-testid="void-super-admin-warning"
                  >
                    Super-admin destructive action on production data. Confirming
                    sends a one-time per-invoice override for this void only.
                  </div>
                )}

                {/* Migrated payment callout */}
                {isMigratedPayment && targetPayment && (
                  <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2.5 text-sm text-amber-800">
                    This payment was migrated from the older system on{' '}
                    {new Date(targetPayment.created_at).toLocaleDateString('en-US', {
                      month: 'short', day: 'numeric', year: 'numeric',
                    })}. Voiding it will mark the original recorded payment as zero — make sure that&apos;s what you want.
                  </div>
                )}

                {/* v0.14 (Dmitri sweep): when exactly one active payment, surface a
                    friendly summary of what's being voided so Christa never
                    silently voids the wrong row. Picker (radios) only shows
                    when >1 active. */}
                {sortedActive.length === 1 && targetPayment && (
                  <div
                    className="rounded-md bg-gray-50 border border-gray-200 px-3 py-2.5 text-sm text-gray-700"
                    data-testid="void-payment-single-summary"
                  >
                    Voiding{' '}
                    <span className="font-medium">
                      {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(targetPayment.amount)}
                    </span>
                    {' '}recorded on{' '}
                    <span className="font-medium">
                      {formatDateOnly(targetPayment.paid_at, { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                    {(() => {
                      const label = targetPayment.recorded_by_email
                        ?? (targetPayment.recorded_by ? 'Unknown user' : null);
                      return label ? <> by <span className="font-medium">{label}</span></> : null;
                    })()}
                    .
                  </div>
                )}

                {/* Payment picker (only when >1 active payment) */}
                {sortedActive.length > 1 && (
                  <fieldset>
                    <legend className="block text-sm font-medium text-gray-700 mb-1">
                      Select payment to void:
                    </legend>
                    <div className="space-y-2">
                      {sortedActive.map((p: any) => {
                        // Theme 6b S2 follow-up: prefer the backend-resolved email over the raw
                        // Firebase UID. Dmitri sweep #2 (2026-05-27): if the backend cannot
                        // resolve UID → email (deleted Firebase user, lookup race), fall back
                        // to "Unknown user" instead of leaking the raw UID — the regression
                        // cad71c79 was supposed to prevent. Reserve "Backfill" for true
                        // recorded_by IS NULL.
                        const recorderLabel = p.recorded_by_email
                          ?? (p.recorded_by ? 'Unknown user' : null);
                        return (
                          <label key={p.id} className="flex items-center gap-2 cursor-pointer text-sm">
                            <input
                              type="radio"
                              name="void-payment-id"
                              value={p.id}
                              checked={(selectedPaymentId ?? sortedActive[0]?.id) === p.id}
                              onChange={() => setSelectedPaymentId(p.id)}
                              className="accent-blue-600"
                            />
                            <span>
                              {/* v0.14 (Dmitri sweep): paid_at is date-only; render via
                                  formatDateOnly to avoid the UTC shift that pushed it
                                  back one day in negative-UTC timezones. */}
                              {formatDateOnly(p.paid_at, { month: 'short', day: 'numeric', year: 'numeric' })}{' '}
                              &mdash;{' '}
                              {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(p.amount)}
                              {recorderLabel ? ` (${recorderLabel})` : ' (Backfill)'}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </fieldset>
                )}

                {/* Void reason textarea */}
                <div>
                  <label htmlFor="void-reason" className="block text-sm font-medium text-gray-700 mb-1">
                    Reason for voiding <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="void-reason"
                    rows={3}
                    value={voidReason}
                    onChange={(e) => setVoidReason(e.target.value)}
                    placeholder="Why are you voiding this payment? (e.g., recorded by mistake, refunded to client)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    data-testid="void-reason-input"
                  />
                </div>

                {/* v0.14 (Dmitri sweep): post-void status message is now computed
                    dynamically. Old code hardcoded "revert to sent" even when the
                    actual outcome was Partially Paid (or draft, or anything else
                    from previous_status). Now: if any active payment remains,
                    the invoice stays Partially Paid; otherwise we surface
                    previous_status — falling back to "Sent" (most common starting
                    status before payment) rather than the vague "prior unpaid
                    status" copy that left Christa guessing.
                    Polish R2 (Dmitri round 2 — M1): when ALL active payments will
                    be voided, the copy MUST name the target status. Default to
                    'sent' when previous_status is null/undefined or unrecognised. */}
                {(() => {
                  const statusLabel = (s: string) =>
                    s.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                  const ALLOWED_PREV: ReadonlyArray<string> = ['sent', 'ready', 'draft', 'cancelled'];
                  let msg: string;
                  if (remainingActiveCount > 0) {
                    msg = 'The invoice will remain Partially Paid.';
                  } else {
                    // 2026-06-03: previous_status is now declared in invoiceSchema —
                    // before that, Zod stripped it and this ALWAYS fell back to 'sent'.
                    const prevRaw = invoice.previous_status;
                    const prev = prevRaw && ALLOWED_PREV.includes(prevRaw) ? prevRaw : 'sent';
                    msg = `The invoice will return to ${statusLabel(prev)}.`;
                  }
                  return (
                    <p className="text-sm font-medium text-orange-600" data-testid="void-post-status-msg">
                      {msg}
                    </p>
                  );
                })()}
              </div>

              <DialogFooter>
                <DialogClose asChild>
                  <button
                    type="button"
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </DialogClose>
                <button
                  type="button"
                  // 2026-05-30 audit fix: trim() before length check. Was
                  // voidReason.length < 3 which allowed 5 spaces "     " to
                  // satisfy the gate; backend correctly rejected but the FE
                  // fired a request that was guaranteed to fail.
                  disabled={voidReason.trim().length < 3 || voidInvoicePaymentMutation.isPending}
                  onClick={() => {
                    // 2026-05-30 audit fix: re-entry guard. Beats button.disabled
                    // on rapid double-click before isPending re-renders.
                    if (voidSubmittingRef.current || voidInvoicePaymentMutation.isPending) return;
                    if (voidReason.trim().length < 3) return;
                    voidSubmittingRef.current = true;
                    voidInvoicePaymentMutation.mutate(
                      {
                        payment_id: targetPaymentId,
                        void_reason: voidReason.trim(),
                        // Super-admin destructive-op guard: voiding a payment is a
                        // guarded destructive op. This void dialog is already an
                        // explicit, reason-required confirm, so for a super-admin
                        // we attach the per-invoice X-Super-Admin-Confirm-Destructive
                        // header (carrying the invoice id) on this request so the
                        // backend guard allows it instead of 403ing. Non-super-admins
                        // pass undefined and are unaffected.
                        ...(isSuperAdmin ? { confirmDestructiveEntityId: id! } : {}),
                      },
                      {
                        onSuccess: () => {
                          voidSubmittingRef.current = false;
                          setShowVoidPaymentDialog(false);
                          setVoidReason('');
                          setSelectedPaymentId(undefined);
                        },
                        onError: () => {
                          voidSubmittingRef.current = false;
                        },
                      },
                    );
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="void-payment-submit"
                >
                  {voidInvoicePaymentMutation.isPending ? 'Voiding…' : 'Void Payment'}
                </button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        );
      })()}

      {/* Super-admin destructive-op guard: confirm a guarded status downgrade
          before firing the PUT with the per-invoice override header. Only a
          super-admin reaches this state (set in InvoiceStatusSelect.onChange). */}
      <SuperAdminDestructiveConfirmDialog
        open={pendingDowngradeStatus !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDowngradeStatus(null);
        }}
        opDescription={
          pendingDowngradeStatus
            ? `downgrade ${statusLabel(invoice.status)} → ${statusLabel(pendingDowngradeStatus)}`
            : ''
        }
        entityLabel={`invoice ${invoice.invoice_number}`}
        confirmLabel="Confirm status change"
        isPending={updateInvoiceInline.isPending}
        onConfirm={() => {
          if (!pendingDowngradeStatus) return;
          const newStatus = pendingDowngradeStatus;
          updateInvoiceInline.mutate(
            {
              id: id!,
              data: { status: newStatus },
              // Per-invoice override header for this confirmed downgrade only.
              confirmDestructiveEntityId: id!,
            },
            {
              onSettled: () => setPendingDowngradeStatus(null),
            },
          );
        }}
      />

      {/* 9.15b: Edit Contact Modal */}
      {invoice.client_contact_id && invoice.organization_id && (
        <EditContactModal
          open={editContactModalOpen}
          contact={{
            id: invoice.client_contact_id,
            organization_id: invoice.organization_id,
            name: invoice.client_contact_name || '',
            email: invoice.client_contact_email,
            phone: invoice.client_contact_phone,
            title: invoice.client_contact_title,
            is_primary_contact: invoice.client_is_primary_contact,
          }}
          onOpenChange={setEditContactModalOpen}
          onContactUpdated={() => {
            // Phase 9.19e: Invalidate combined query
            queryClient.invalidateQueries({ queryKey: ['invoices', id, 'full'] });
          }}
        />
      )}

      {/* 9.18j: Add Contact Modal */}
      {invoice.organization_id && (
        <AddContactModal
          open={showAddContactModal}
          organizationId={invoice.organization_id}
          organizationName={invoice.client_name}
          projectId={invoice.project_id}  // 9.21i: Auto-add to client scope
          onOpenChange={setShowAddContactModal}
          onContactAdded={() => {
            // Phase 9.19e: Invalidate combined query (includes contacts)
            queryClient.invalidateQueries({ queryKey: ['invoices', id, 'full'] });
          }}
          onContactCreated={(contactId, contactName, contactEmail) => {
            // Auto-assign the new contact to this invoice with display fields for optimistic UI
            updateInvoiceInline.mutate({
              id: id!,
              data: {
                contact_id: contactId,
                client_contact_id: contactId,
                client_contact_name: contactName,
                client_contact_email: contactEmail || null,
              } as any
            });
          }}
        />
      )}

      {/* 9.18j: Contact Edit Confirmation Dialog */}
      <AlertDialog open={showContactEditConfirm} onOpenChange={setShowContactEditConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Edit Contact</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>You are about to edit the contact record for <strong>{invoice?.client_contact_name}</strong>.</p>
                <p className="text-amber-600 font-medium">
                  Changes will apply everywhere this contact is used (other invoices, project scopes, etc.).
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowContactEditConfirm(false);
                setEditContactModalOpen(true);
              }}
              data-testid="confirm-contact-edit"
            >
              Continue to Edit
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 9.31c: Pre-edit scope-warning modal — confirms before opening the legal-name editor */}
      <AlertDialog
        open={showLegalNameScopeWarning !== null}
        onOpenChange={(open) => { if (!open) setShowLegalNameScopeWarning(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Edit legal name?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  This changes the legal name on{' '}
                  <strong>
                    ALL invoices
                    {showLegalNameScopeWarning === 'establishment'
                      ? ` for establishment "${invoice?.establishment_name}"`
                      : ` for ${invoice?.client_name}`}
                  </strong>
                  , not just this one. Continue?
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="legal-name-scope-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="legal-name-scope-confirm"
              onClick={() => {
                if (showLegalNameScopeWarning === 'establishment') {
                  setEditedEstablishmentLegalName(invoice?.establishment_legal_name || '');
                  setIsEditingEstablishmentLegalName(true);
                } else {
                  setEditedLegalName(invoice?.client_legal_name || '');
                  setIsEditingLegalName(true);
                }
                setShowLegalNameScopeWarning(null);
              }}
            >
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Hotfix2 — Bug #5: deleted the v0.12.22 Fix 2 "Apply this change
          to the project too?" dialog (scope-establishment) and the
          v0.12.22 Fix 4 "Save this address upstream?" dialog (address-to-
          project / address-to-establishment). Both were R4-superseded by
          BillToPanel + PropagationModal. State setters were never called
          from any code path after R4 landed — confirmed via grep before
          removal. */}

      {/* 9.26: Establishment Legal Name Confirmation Dialog */}
      <AlertDialog open={showEstablishmentLegalConfirm} onOpenChange={setShowEstablishmentLegalConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Update Establishment Legal Name?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>This will update the establishment record for <strong>{invoice?.establishment_name}</strong>.</p>
                <p className="font-medium">
                  All invoices using this DBA will show the new legal entity name.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingEstablishmentLegalValue('')}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                // 9.26: Optimistic update - update invoice cache immediately
                const newLegalName = pendingEstablishmentLegalValue || null;

                // Cancel any pending invoice queries to prevent race conditions
                await queryClient.cancelQueries({ queryKey: ['invoices', id, 'full'] });

                queryClient.setQueryData(['invoices', id, 'full'], (old: any) => {
                  if (!old || !old.invoice) return old;
                  // Update both establishment_legal_name and client_legal_name (since it's the effective value)
                  return {
                    ...old,
                    invoice: {
                      ...old.invoice,
                      establishment_legal_name: newLegalName,
                      client_legal_name: newLegalName
                    }
                  };
                });
                setShowEstablishmentLegalConfirm(false);

                updateEstablishment.mutate({
                  organizationId: invoice!.organization_id!,
                  establishmentId: invoice!.establishment_id!,
                  data: { legal_name: newLegalName }
                }, {
                  onSuccess: () => {
                    // Don't invalidate on success - optimistic update is already correct
                    setPendingEstablishmentLegalValue('');
                  },
                  onError: () => {
                    // Revert on error by refetching from server
                    queryClient.invalidateQueries({ queryKey: ['invoices', id, 'full'] });
                    setPendingEstablishmentLegalValue('');
                  }
                });
              }}
              data-testid="confirm-establishment-legal-update"
            >
              {updateEstablishment.isPending ? 'Updating...' : 'Update Legal Name'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 9.21g: Address Confirmation Dialog - handles both organization and project address editing */}
      <AlertDialog open={showAddressConfirm} onOpenChange={(open) => {
        if (!open) {
          setShowAddressConfirm(false);
          setPendingAddressValue('');
          setPendingAddressSource(null);
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Edit {pendingAddressSource === 'project' ? 'Project Billing' : 'Organization'} Address
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  {pendingAddressSource === 'project'
                    ? "Edit the project's billing address (used as the default Bill To on this project's invoices)."
                    : "Edit the organization's billing address."}
                </p>
                <textarea
                  value={pendingAddressValue}
                  onChange={(e) => setPendingAddressValue(e.target.value)}
                  placeholder="Enter address"
                  className="w-full min-h-[100px] p-2 border rounded resize-y text-sm text-foreground"
                  autoFocus
                  data-testid="address-edit-input"
                />
                <p className="text-sm text-amber-600 font-medium">
                  {pendingAddressSource === 'project'
                    ? "This updates the project's billing address. The site address (shown on proposals and used for weather) is unchanged. All invoices using project billing address will reflect this change."
                    : `This will update the organization record. All invoices for ${invoice?.client_name} using organization address will show this.`}
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={updateOrganization.isPending || updateProject.isPending}
              onClick={async () => {
                const newAddress = pendingAddressValue || null;

                // Cancel any pending invoice queries to prevent race conditions
                await queryClient.cancelQueries({ queryKey: ['invoices', id, 'full'] });

                if (pendingAddressSource === 'project') {
                  // Update project address
                  queryClient.setQueryData(['invoices', id, 'full'], (old: any) => {
                    if (!old || !old.invoice) return old;
                    return { ...old, invoice: { ...old.invoice, project_address: newAddress } };
                  });
                  setShowAddressConfirm(false);

                  // v0.12.24: Write to billing_address (project-scoped billing
                  // override). Site address (projects.address) used by proposal
                  // PDFs, draw certs, and weather geocoding stays untouched.
                  updateProject.mutate({
                    projectId: invoice!.project_id,
                    data: { billing_address: newAddress }
                  }, {
                    onSuccess: () => {
                      setPendingAddressValue('');
                      setPendingAddressSource(null);
                    },
                    onError: () => {
                      queryClient.invalidateQueries({ queryKey: ['invoices', id, 'full'] });
                      setPendingAddressValue('');
                      setPendingAddressSource(null);
                    }
                  });
                } else {
                  // Update organization billing address
                  queryClient.setQueryData(['invoices', id, 'full'], (old: any) => {
                    if (!old || !old.invoice) return old;
                    return { ...old, invoice: { ...old.invoice, org_billing_address: newAddress } };
                  });
                  setShowAddressConfirm(false);

                  updateOrganization.mutate({
                    id: invoice!.organization_id!,
                    data: { billing_address: newAddress }
                  }, {
                    onSuccess: () => {
                      setPendingAddressValue('');
                      setPendingAddressSource(null);
                    },
                    onError: () => {
                      queryClient.invalidateQueries({ queryKey: ['invoices', id, 'full'] });
                      setPendingAddressValue('');
                      setPendingAddressSource(null);
                    }
                  });
                }
              }}
              data-testid="confirm-address-update"
            >
              {(updateOrganization.isPending || updateProject.isPending) ? 'Updating...' : 'Update Address'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 9.18j: Establishment Name Edit Confirmation Dialog */}
      <AlertDialog open={showEstablishmentConfirm} onOpenChange={(open) => {
        if (!open) {
          setShowEstablishmentConfirm(false);
          setEditingEstablishmentId(null);
          setPendingEstablishmentName('');
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Edit Establishment Name</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>Enter the new name for this establishment (DBA):</p>
                <Input
                  value={pendingEstablishmentName}
                  onChange={(e) => setPendingEstablishmentName(e.target.value)}
                  placeholder="Establishment name"
                  className="mt-2"
                  autoFocus
                  data-testid="edit-establishment-name-input"
                />
                <p className="text-sm text-amber-600 font-medium">
                  This will update the establishment name. All invoices using this establishment will show the new name.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={!pendingEstablishmentName.trim() || updateEstablishment.isPending}
              onClick={() => {
                if (editingEstablishmentId && pendingEstablishmentName.trim()) {
                  const newName = pendingEstablishmentName.trim();
                  // 9.18j: Optimistic update - update invoice cache immediately
                  // Phase 9.19e: Use combined query key and nested structure
                  queryClient.setQueryData(['invoices', id, 'full'], (old: any) => {
                    if (!old || !old.invoice) return old;
                    return { ...old, invoice: { ...old.invoice, establishment_name: newName } };
                  });
                  setShowEstablishmentConfirm(false);

                  updateEstablishment.mutate({
                    organizationId: invoice!.organization_id!,
                    establishmentId: editingEstablishmentId,
                    data: { name: newName }
                  }, {
                    onSuccess: () => {
                      // Don't invalidate invoice on success - optimistic update is already correct
                      // Phase 9.19e: Combined query now includes establishments, no separate invalidation needed
                      setEditingEstablishmentId(null);
                      setPendingEstablishmentName('');
                    },
                    onError: () => {
                      // Revert on error by refetching from server
                      // Phase 9.19e: Use combined query key
                      queryClient.invalidateQueries({ queryKey: ['invoices', id, 'full'] });
                      setEditingEstablishmentId(null);
                      setPendingEstablishmentName('');
                    }
                  });
                }
              }}
              data-testid="confirm-establishment-name-update"
            >
              {updateEstablishment.isPending ? 'Updating...' : 'Update Name'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 9.18b: Remove Item Confirmation Dialog */}
      <AlertDialog open={!!removeItemId} onOpenChange={(open) => !open && setRemoveItemId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Item from Invoice</AlertDialogTitle>
            <AlertDialogDescription>
              Remove from invoice? The billable item will remain on the project.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (removeItemId) {
                  removeItemMutation.mutate(removeItemId);
                  setRemoveItemId(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-remove-item"
            >
              {removeItemMutation.isPending ? 'Removing...' : 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Accounting Contacts — manage sheet.
          When the invoice has an establishment, show its contacts FIRST (those are
          the ones that actually drive this invoice's recipient). The org-level list
          renders after as the fallback used when establishment contacts are empty. */}
      {invoice?.organization_id && (
        <Sheet open={manageAccountingOpen} onOpenChange={setManageAccountingOpen}>
          <SheetContent side="right" className="w-full sm:max-w-md flex flex-col p-0">
            <SheetHeader className="px-4 pt-4 pb-3 border-b">
              <SheetTitle>Accounting Contacts</SheetTitle>
              {invoice.establishment_name && (
                <p className="text-xs text-muted-foreground">
                  for {invoice.establishment_name} ({invoice.client_name})
                </p>
              )}
            </SheetHeader>
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {invoice.establishment_id && (
                <AccountingContactsList
                  orgId={invoice.organization_id}
                  establishmentId={invoice.establishment_id}
                  overrideNotice={true}
                  testId={`invoice-est-${invoice.establishment_id}-accounting-contacts`}
                />
              )}
              <AccountingContactsList
                orgId={invoice.organization_id}
                title={invoice.establishment_id ? 'Default contacts (used when an establishment has none)' : undefined}
                testId="invoice-accounting-contacts"
              />
              <a
                href={`/organizations/${invoice.organization_id}/edit`}
                className="text-xs text-blue-600 hover:underline inline-block"
              >
                Open organization settings →
              </a>
            </div>
          </SheetContent>
        </Sheet>
      )}

      {/* R3: Schedule Follow-up Modal */}
      <ScheduleFollowupModal
        open={scheduleFollowupOpen}
        invoiceId={invoice.id}
        onOpenChange={setScheduleFollowupOpen}
      />

      {/* Delete invoice confirmation dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Invoice</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete invoice "{invoice?.invoice_number}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                deleteMutation.mutate(id!, {
                  onSuccess: () => navigate(-1),
                });
                setShowDeleteConfirm(false);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* SPEC-invoice-merge-history R4: choose the other invoice (same project, original-number search), then
          review. Reads the server plan; sends nothing until the reviewer confirms. */}
      {showMergeReview && (
        <InvoiceMergeFlow
          anchor={{
            id: invoice.id,
            invoice_number: invoice.invoice_number,
            project_id: invoice.project_id,
            project_name: invoice.project_name,
          }}
          onClose={() => setShowMergeReview(false)}
          onCancelInvoice={(invoiceId) => {
            setShowMergeReview(false);
            if (invoiceId === invoice.id) setShowCancelDialog(true);
            else navigate(`/invoices/${invoiceId}`);
          }}
          onMerged={() => {
            // The review keeps showing the committed result with an "Open invoice" link; just refresh data.
            queryClient.invalidateQueries({ queryKey: ['invoices'] });
          }}
        />
      )}

      {/* Cancel-invoice feature. */}
      <CancelInvoiceDialog
        open={showCancelDialog}
        onOpenChange={(open) => {
          setShowCancelDialog(open);
          if (open) setCancelError(null);
        }}
        invoiceNumber={invoice.invoice_number}
        projectName={invoice.project_name}
        clientName={invoice.client_name}
        totalAmount={invoice.total_amount}
        qboExportedAt={invoice.qbo_csv_exported_at ?? null}
        pending={cancelMutation.isPending}
        error={cancelError}
        onConfirm={(reason) => {
          setCancelError(null);
          if (isSuperAdmin && isGuardedStatusDowngrade(invoice.status, 'cancelled')) {
            // Same two-step pattern as the status-select downgrade flow
            // below: close this dialog, open the super-admin confirm, and
            // fire the mutation only once THAT is confirmed.
            setPendingCancelSuperAdmin({ reason });
            setShowCancelDialog(false);
            return;
          }
          cancelMutation.mutate(
            { id: id!, data: { reason } },
            {
              onSuccess: () => setShowCancelDialog(false),
              onError: (err: any) => setCancelError({ message: err?.message || 'Failed to cancel invoice' }),
            },
          );
        }}
      />
      <SuperAdminDestructiveConfirmDialog
        open={pendingCancelSuperAdmin !== null}
        onOpenChange={(open) => {
          if (!open) setPendingCancelSuperAdmin(null);
        }}
        opDescription={`cancel ${statusLabel(invoice.status)} invoice`}
        entityLabel={`invoice ${invoice.invoice_number}`}
        confirmLabel="Confirm cancel"
        isPending={cancelMutation.isPending}
        onConfirm={() => {
          if (!pendingCancelSuperAdmin) return;
          cancelMutation.mutate(
            {
              id: id!,
              data: { reason: pendingCancelSuperAdmin.reason },
              confirmDestructiveEntityId: id!,
            },
            { onSettled: () => setPendingCancelSuperAdmin(null) },
          );
        }}
      />
    </DashboardLayout>
  );
}
